import { useEffect, useState } from 'react';
import { Phone, Mail, Users, StickyNote, CalendarClock, Send, Loader2, Sparkles, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CATEGORY_LABEL, formatDistance } from '@/modules/sales-research/lib';
import type { CompanyCategory } from '@/modules/sales-research/types';
import { useIsPrivileged } from '@/hooks/useAuth';
import { useCompanyActivities, useCompanyContacts, useCompanyDrafts, useCrmAction, useRunDraftAgent, useRunEnrich, useCancelJob } from './api';
import { Drawer, Select, Textarea, TextInput, Field, relTime } from './widgets';
import { STAGE_LABEL, STAGE_ORDER } from './types';
import type { PipelineRow } from './types';

const ACTION_ICON: Record<string, typeof Phone> = {
  call: Phone, email: Mail, whatsapp: Send, meeting: Users, note: StickyNote,
  visit: Users, linkedin: Users,
};

export function CompanyDrawer({ row, onClose, onChanged, managerNames = [] }: {
  row: PipelineRow | null; onClose: () => void; onChanged: () => void; managerNames?: string[];
}) {
  const open = !!row;
  const activitiesQ = useCompanyActivities(row?.id);
  const draftsQ = useCompanyDrafts(row?.id);
  const act = useCrmAction();
  const runAgent = useRunDraftAgent();

  const [kind, setKind] = useState('call');
  const [note, setNote] = useState('');
  const [followup, setFollowup] = useState('');
  const [manager, setManager] = useState('');

  useEffect(() => { setManager(row?.assigned_name ?? ''); }, [row?.id, row?.assigned_name]);

  if (!row) return null;

  async function saveManager(name: string) {
    setManager(name);
    await act.mutateAsync({ action: 'assign', company_id: row!.id, assigned_name: name || null });
    onChanged();
  }

  async function changeStage(stage: string) {
    await act.mutateAsync({ action: 'set_stage', company_id: row!.id, stage });
    onChanged();
  }
  async function logIt() {
    if (!note.trim() && !followup) return;
    await act.mutateAsync({
      action: 'log_activity', company_id: row!.id, kind, body: note.trim() || null,
      direction: kind === 'note' ? undefined : 'outbound',
      next_followup_at: followup ? new Date(followup).toISOString() : undefined,
    });
    setNote(''); setFollowup('');
    activitiesQ.refetch();
    onChanged();
  }
  async function draftForCompany() {
    await runAgent.mutateAsync({ company_ids: [row!.id], limit: 1 });
    draftsQ.refetch();
    onChanged();
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <span className="truncate">{row.name}</span>
          <Badge variant="outline" className="font-normal">{CATEGORY_LABEL[row.category as CompanyCategory]}</Badge>
        </div>
      }
    >
      <div className="space-y-5">
        {/* facts */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <Fact label="City" value={row.primary_city ?? '—'} />
          <Fact label="Score" value={row.score != null ? String(row.score) : '—'} />
          <Fact label="Nearest outlet" value={row.nearest_outlet_name ?? '—'} />
          <Fact label="Distance" value={formatDistance(row.nearest_distance_m)} />
          <Fact label="Warm orders (90d)" value={row.warm_orders ? String(row.warm_orders) : '—'} />
          <Fact label="Contacts" value={String(row.contact_count)} />
        </div>

        {/* stage */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Stage</span>
          <Select
            value={row.status}
            onChange={changeStage}
            options={STAGE_ORDER.concat(['disqualified'] as any).map((s) => ({ value: s, label: STAGE_LABEL[s] ?? s }))}
          />
          {row.next_followup_at && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5" /> follow-up {relTime(row.next_followup_at)}
            </span>
          )}
        </div>

        {/* sales manager (owner) */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Sales manager</span>
          <input
            list="drawer-mgr-names"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            onBlur={(e) => { if (e.target.value !== (row.assigned_name ?? '')) saveManager(e.target.value); }}
            placeholder="Assign an owner…"
            className="h-8 flex-1 rounded-md border border-border/60 bg-transparent px-2.5 text-sm outline-none focus:border-[#51BFAA]"
          />
          <datalist id="drawer-mgr-names">{managerNames.map((n) => <option key={n} value={n} />)}</datalist>
        </div>

        {/* contacts */}
        <ContactsSection companyId={row.id} />

        {/* drafts */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Outreach drafts</h3>
            <Button size="sm" variant="outline" disabled={runAgent.isPending} onClick={draftForCompany}>
              {runAgent.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Draft with AI
            </Button>
          </div>
          {(draftsQ.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No drafts yet.</p>
          ) : (
            <div className="space-y-1.5">
              {(draftsQ.data ?? []).map((d) => (
                <DraftRow key={d.id} draft={d} onChanged={() => { draftsQ.refetch(); onChanged(); }} />
              ))}
            </div>
          )}
        </section>

        {/* log activity */}
        <section className="space-y-2 rounded-lg border border-border/60 p-3">
          <h3 className="text-sm font-semibold">Log an interaction</h3>
          <div className="flex items-center gap-2">
            <Select
              value={kind}
              onChange={setKind}
              options={[
                { value: 'call', label: '📞 Call' }, { value: 'email', label: '✉️ Email' },
                { value: 'whatsapp', label: '💬 WhatsApp' }, { value: 'meeting', label: '🤝 Meeting' },
                { value: 'visit', label: '🏢 Visit' }, { value: 'note', label: '📝 Note' },
              ]}
            />
            <Field label="Next follow-up">
              <TextInput type="date" value={followup} onChange={(e) => setFollowup(e.target.value)} className="h-8" />
            </Field>
          </div>
          <Textarea rows={3} placeholder="What happened? (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end">
            <Button size="sm" disabled={act.isPending} onClick={logIt}>
              {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Log
            </Button>
          </div>
        </section>

        {/* timeline */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Timeline</h3>
          {(activitiesQ.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No activity yet.</p>
          ) : (
            <ol className="space-y-2.5">
              {(activitiesQ.data ?? []).map((a) => {
                const Icon = ACTION_ICON[a.action] ?? StickyNote;
                return (
                  <li key={a.id} className="flex gap-2.5 text-xs">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{labelFor(a.action)}</span>
                        <span className="text-muted-foreground">{relTime(a.occurred_at)}</span>
                        <span className="ml-auto truncate text-muted-foreground">{a.actor}</span>
                      </div>
                      {a.subject && <p className="text-foreground/80">{a.subject}</p>}
                      {a.body && <p className="whitespace-pre-wrap text-muted-foreground">{a.body}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </Drawer>
  );
}

function DraftRow({ draft, onChanged }: { draft: any; onChanged: () => void }) {
  const act = useCrmAction();
  const [open, setOpen] = useState(false);
  const pending = draft.status === 'pending';

  const approved = draft.status === 'approved';
  async function approve() {
    await act.mutateAsync({ action: 'approve_draft', draft_id: draft.id });
    await act.mutateAsync({ action: 'send_draft', draft_id: draft.id }); // -> Gmail Drafts (no-op if Gmail off)
    onChanged();
  }
  async function toGmail() { await act.mutateAsync({ action: 'send_draft', draft_id: draft.id }); onChanged(); }
  async function reject() { await act.mutateAsync({ action: 'reject_draft', draft_id: draft.id, reason: 'rejected from company view' }); onChanged(); }

  return (
    <div className="rounded-md border border-border/60 text-xs">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left">
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <Badge variant={draft.status === 'approved' ? 'positive' : draft.status === 'rejected' ? 'negative' : 'muted'}>{draft.status === 'scheduled' ? 'in gmail drafts' : draft.status}</Badge>
        <span className="truncate font-medium">{draft.subject}</span>
        <span className="ml-auto shrink-0 text-muted-foreground">{relTime(draft.created_at)}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-border/60 px-2.5 py-2">
          <div className="text-muted-foreground">
            To: {draft.to_email ?? <span className="text-[#B15599]">no recipient — add a contact email</span>}
          </div>
          <p className="whitespace-pre-wrap leading-relaxed text-foreground/90">{draft.body}</p>
          {pending && (
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" disabled={act.isPending} onClick={reject}><X className="h-3.5 w-3.5" /> Reject</Button>
              <Button size="sm" disabled={act.isPending} onClick={approve}>
                {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve & save to Gmail
              </Button>
            </div>
          )}
          {approved && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <span className="mr-auto text-2xs text-muted-foreground">Approved — not yet in Gmail Drafts</span>
              <Button size="sm" disabled={act.isPending} onClick={toGmail}>
                {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Save to Gmail Drafts
              </Button>
            </div>
          )}
          {draft.status === 'scheduled' && (
            <div className="flex items-center gap-1.5 pt-1 text-2xs text-[#51BFAA]">
              <Check className="h-3.5 w-3.5" /> In your Gmail Drafts — open Gmail to review &amp; send.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ROLES = ['admin', 'hr', 'facilities', 'office_manager', 'people_ops', 'procurement', 'founder', 'other'];

function ContactsSection({ companyId }: { companyId: string }) {
  const contactsQ = useCompanyContacts(companyId);
  const act = useCrmAction();
  const enrich = useRunEnrich();
  const cancelJob = useCancelJob();
  const privileged = useIsPrivileged();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<any>({ role_category: 'admin' });
  const set = (k: string, v: any) => setF((s: any) => ({ ...s, [k]: v }));
  const contacts = contactsQ.data ?? [];

  async function findContacts() {
    await enrich.mutateAsync({ company_ids: [companyId], limit: 1 });
    contactsQ.refetch();
  }

  async function add() {
    if (!f.full_name && !f.email) return;
    await act.mutateAsync({
      action: 'create_contact', company_id: companyId,
      full_name: f.full_name || null, title: f.title || null, role_category: f.role_category,
      email: f.email || null, phone: f.phone || null, is_primary: contacts.length === 0,
    });
    setF({ role_category: 'admin' }); setAdding(false);
    contactsQ.refetch();
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Contacts</h3>
        <div className="flex items-center gap-1">
          {privileged && (enrich.isPending ? (
            <Button size="sm" variant="ghost" onClick={() => cancelJob.mutate('enrich')} disabled={cancelJob.isPending}>
              <X className="h-3.5 w-3.5" /> Stop
            </Button>
          ) : (
            <Button size="sm" variant="ghost" disabled={enrich.isPending} onClick={findContacts}>
              Find contacts
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setAdding((v) => !v)}>{adding ? 'Close' : '+ Add'}</Button>
        </div>
      </div>
      {contacts.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">
          {enrich.data && (enrich.data.contacts_created ?? 0) === 0
            ? 'No public contacts found on the website — add one by hand.'
            : 'No contacts yet — “Find contacts” or add a decision-maker.'}
        </p>
      )}
      {contacts.map((c: any) => (
        <div key={c.id} className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-1.5 text-xs">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{c.full_name ?? '(no name)'}</span>
              {c.is_primary && <Badge variant="muted">primary</Badge>}
              <span className="text-muted-foreground">{c.role_category}</span>
            </div>
            {c.title && <div className="text-muted-foreground">{c.title}</div>}
            <div className="flex flex-wrap gap-x-2 text-muted-foreground">
              {c.email && <span>{c.email}</span>}
              {c.phone && <span>{c.phone}</span>}
            </div>
          </div>
        </div>
      ))}
      {adding && (
        <div className="space-y-2 rounded-md border border-border/60 p-2.5">
          <div className="grid grid-cols-2 gap-2">
            <TextInput placeholder="Name" value={f.full_name ?? ''} onChange={(e) => set('full_name', e.target.value)} className="h-8" />
            <TextInput placeholder="Title" value={f.title ?? ''} onChange={(e) => set('title', e.target.value)} className="h-8" />
            <Select value={f.role_category} onChange={(v) => set('role_category', v)} className="w-full"
              options={ROLES.map((r) => ({ value: r, label: r.replace('_', ' ') }))} />
            <TextInput placeholder="Phone" value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} className="h-8" />
          </div>
          <TextInput placeholder="Email" type="email" value={f.email ?? ''} onChange={(e) => set('email', e.target.value)} className="h-8" />
          <div className="flex justify-end">
            <Button size="sm" disabled={act.isPending} onClick={add}>Add contact</Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function labelFor(action: string): string {
  const map: Record<string, string> = {
    lead_created: 'Lead created', company_updated: 'Company updated', stage_changed: 'Stage changed',
    contact_added: 'Contact added', assigned: 'Assigned', followup_scheduled: 'Follow-up scheduled',
    draft_generated: 'Draft generated', draft_edited: 'Draft edited', draft_approved: 'Draft approved',
    draft_rejected: 'Draft rejected', send_pending: 'Send queued', email_sent: 'Email sent',
    gmail_draft_created: 'Saved to Gmail Drafts', gmail_draft_failed: 'Gmail draft failed',
    reply_received: 'Reply received', suppressed: 'Suppressed',
    call: 'Call', email: 'Email', whatsapp: 'WhatsApp', meeting: 'Meeting', visit: 'Visit', note: 'Note',
  };
  return map[action] ?? action;
}
